import LockIcon from '@mui/icons-material/Lock';
import { Autocomplete, Box, Button, Chip, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import * as yup from 'yup';

import FlipCard from '../../../animations/FlipCard/FlipCard';
import { MAX_REDEMPTION_LIMIT } from '../../../constants/oneLink';
import {
  ALLOWED_DOMAINS,
  EMAIL_DOMAIN_REGEX,
  ENABLED_2FA,
  PASSWORD,
  REDEMPTION_LIMIT,
} from '../../../constants/shipping';
import { IOneLinkChunkFormProps } from '../../../contexts/OneLinkSummaryProvider';
import { isObjectsEqual } from '../../../utils/helpers';
import { handleScrollListUpdate } from '../../../utils/oneLink';
import { FormControls } from '../../SummaryComponentMUI/components/common';
import { CopyableButton, TwoFaAlert } from '../../_mui';

const SecurityForm = forwardRef<boolean | null, IOneLinkChunkFormProps>(({ onSubmit, instance, onClose }, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [warning, setWarning] = React.useState(false);
  const [showAlert, setShowAlert] = React.useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevDomainsLengthRef = useRef(0);
  const {
    palette: { common },
  } = useTheme();

  const StyledAutocomplete = styled(Autocomplete)(() => ({
    '& .MuiAutocomplete-inputRoot': {
      display: 'flex',
      alignItems: 'flex-start',
      alignContent: 'flex-start',
      minHeight: 135,
      maxHeight: 160,
    },

    '& .MuiFormHelperText-root': {
      marginTop: 0,
    },
  }));

  const validationSchema = yup.object().shape({
    password: yup.string().test('no-spaces', 'Password must not contain spaces.', (value) => {
      if (!value) {
        return true;
      }

      return !/\s/.test(value);
    }),
    [ALLOWED_DOMAINS]: yup
      .array()
      .nullable()
      .test('all-domains-valid', 'One or more domains are invalid.', (domains) => {
        if (!Array.isArray(domains) || domains.length === 0) {
          return true;
        }

        return domains.every((domain) => new RegExp(EMAIL_DOMAIN_REGEX).test(domain));
      }),
    max_submit_count: yup
      .number()
      .nullable()
      .min(0, 'Redemption limit cannot be negative')
      .max(MAX_REDEMPTION_LIMIT, 'Redemption limit cannot exceed 9,999,999')
      .test('no-negative-or-decimals', 'Negative numbers and numbers with decimals are not allowed', (value) => {
        if (!value) {
          return true;
        }
        // Reject negative or decimal numbers (number 5.0 is valid, number 5.1 is invalid)
        return (value && Number.isInteger(value) && value >= 0) || value === 0;
      })
      .test(
        'redemption-limit-validation',
        'Redemption limit must exceed number of confirmed sends; increase it or deactivate One Link.',
        (value) => {
          const useTimesCount = instance?.use_times_count || 0;
          return !value || value > useTimesCount;
        },
      ),
    enabled_2fa: yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      password: instance?.password ?? '',
      allowed_domains: instance?.allowed_domains ?? [],
      max_submit_count: instance?.max_submit_count ?? 0,
      enabled_2fa: instance?.enabled_2fa ?? false,
    },
    validationSchema,
    validateOnChange: true,
    onSubmit: (values) => {
      onSubmit({
        security_info: {
          ...values,
          max_submit_count: values.max_submit_count ? Number(values.max_submit_count) : null,
        },
      });
      if (ref && typeof ref !== 'function') {
        ref.current = false;
      }
    },
  });

  const { password, allowed_domains, max_submit_count, enabled_2fa } = formik.values;
  const { max_submit_count: maxSubmitError, password: passwordError } = formik.errors;
  const { setFieldValue, handleChange, handleSubmit, initialValues } = formik;

  const handleConfigureDomainProtection = useCallback(() => {
    setFieldValue(ENABLED_2FA, false);
    setWarning(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  }, [setFieldValue]);

  const warningMessage = useMemo(() => {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Confirm configure Domain Protection
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          If you want to configure Domain Protection, then 2FA will be disabled and the settings will be reset.
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 3.5 }}>
          <Button variant="outlined" onClick={() => setWarning(false)} sx={{ px: 7 }}>
            Cancel
          </Button>
          <Button variant="contained" sx={{ color: common.white, px: 8 }} onClick={handleConfigureDomainProtection}>
            Configure Domain Protection
          </Button>
        </Box>
      </Box>
    );
  }, [handleConfigureDomainProtection, common.white]);

  const handleAllowedDomainsChange = useCallback(
    (value: string[]) => {
      const nextDomains = Array.from(new Set(value));

      handleScrollListUpdate(containerRef, inputRef, prevDomainsLengthRef, nextDomains, (val) =>
        setFieldValue(ALLOWED_DOMAINS, val),
      );
    },
    [allowed_domains, setFieldValue],
  );

  const handleRedemptionLimitChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/^0+/, '') || '0';
      const numValue = Number(value);

      if (numValue > MAX_REDEMPTION_LIMIT) {
        value = String(MAX_REDEMPTION_LIMIT);
      }

      setFieldValue(REDEMPTION_LIMIT, value);
    },
    [setFieldValue],
  );

  const handleTwoFaAlertCancel = useCallback(() => {
    setFieldValue(ENABLED_2FA, true);
    setFieldValue(ALLOWED_DOMAINS, []);
    setShowAlert(false);
  }, [setFieldValue]);

  const handleDomainKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const input = inputRef.current?.value?.trim();

      if (['Enter', ' ', ','].includes(e.key) && input && input.length > 0) {
        handleAllowedDomainsChange([...allowed_domains, input]);
      }
    },
    [allowed_domains, handleAllowedDomainsChange],
  );

  const handleDomainBlur = useCallback(() => {
    const input = inputRef.current?.value?.trim();

    if (input && input.length > 0) {
      handleAllowedDomainsChange([...allowed_domains, input]);
    }
  }, [allowed_domains, handleAllowedDomainsChange]);

  const invalidDomains = useMemo(() => {
    if (allowed_domains) {
      return allowed_domains.filter((domain) => !new RegExp(EMAIL_DOMAIN_REGEX).test(domain));
    }
  }, [allowed_domains]);

  useEffect(() => {
    if (instance?.enabled_2fa && !enabled_2fa) {
      setShowAlert(true);
    }
  }, [instance?.enabled_2fa, enabled_2fa]);

  const handleClose = useCallback(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = false;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = !isObjectsEqual(initialValues, formik.values);
    }
  }, [formik.values, initialValues]);

  return (
    <Box
      component="form"
      role="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
      onSubmit={handleSubmit}
    >
      <Box sx={{ position: 'relative' }}>
        <TextField
          type="number"
          label="Redemption limit"
          name={REDEMPTION_LIMIT}
          value={max_submit_count}
          onChange={handleRedemptionLimitChange}
          variant="outlined"
          fullWidth
          slotProps={{
            htmlInput: {
              step: 1,
              max: MAX_REDEMPTION_LIMIT,
            },
          }}
          sx={{ mb: 4 }}
          error={!!formik.errors.max_submit_count}
          helperText={maxSubmitError}
        />
        <TextField
          label="Password protection"
          variant="outlined"
          name={PASSWORD}
          value={password}
          onChange={handleChange}
          sx={{
            mb: 4,
          }}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ width: 30, justifyContent: 'center' }}>
                  <LockIcon sx={{ width: 16, height: 16, mb: '2px' }} />
                </InputAdornment>
              ),
              endAdornment: password ? (
                <InputAdornment position="end">
                  <CopyableButton value={password} isVisible toastLabel="Password" />
                </InputAdornment>
              ) : null,
            },
          }}
          error={!!passwordError}
          helperText={passwordError}
        />
        <FlipCard
          flipped={warning && enabled_2fa}
          front={
            <StyledAutocomplete
              clearIcon={false}
              options={[]}
              freeSolo
              multiple
              value={allowed_domains || []}
              onChange={(_, value) => handleAllowedDomainsChange(value as string[])}
              onFocus={() => {
                if (enabled_2fa) {
                  setWarning(true);
                }
              }}
              onKeyDown={handleDomainKeyDown}
              onBlur={handleDomainBlur}
              renderTags={(value, getTagProps) => (
                <Box
                  sx={{
                    maxHeight: 116,
                    overflowY: 'auto',
                    marginRight: -4,
                    pr: 4, // compensate visually negative margin
                  }}
                  ref={containerRef}
                >
                  {value.map((domain, index) => {
                    const isInvalid = invalidDomains?.includes(domain as string);
                    const { key, ...tagProps } = getTagProps({ index }); // need to destructure key
                    return (
                      <Chip
                        key={key}
                        variant="outlined"
                        label={domain as string}
                        {...tagProps}
                        sx={{
                          borderColor: isInvalid ? 'error.main' : '',
                          color: isInvalid ? 'error.main' : '',
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  label="Allowed domains"
                  error={!!formik.errors.allowed_domains}
                  helperText={formik.errors.allowed_domains ? formik.errors.allowed_domains : ''}
                  sx={{
                    '& .MuiInputBase-input': {
                      maxHeight: '7px',
                      marginTop: '5px',
                    },
                  }}
                />
              )}
              sx={{ mb: 4 }}
            />
          }
          back={warningMessage}
        />
        {showAlert && <TwoFaAlert onCancel={handleTwoFaAlertCancel} />}
      </Box>
      <FormControls
        onCancel={handleClose}
        isDisabled={!formik.isValid || (ref && typeof ref !== 'function' ? !ref.current : true)}
      />
    </Box>
  );
});

export default SecurityForm;
